import React, { useState } from 'react';
import SearchableTicker from '../../SearchableTicker';
import { INTEL_COMPARE_MODES, MAX_COMPARE_PEERS } from '../portfolioConstants';

const MODE_OPTIONS = [
  {
    key: INTEL_COMPARE_MODES.NONE,
    label: 'None',
    title: 'Analyse this holding on its own',
  },
  {
    key: INTEL_COMPARE_MODES.COMPETITORS,
    label: 'Competitors',
    title: 'Analyse it against the competitor set you save for it',
  },
  {
    key: INTEL_COMPARE_MODES.HOLDINGS,
    label: 'My Holdings',
    title: 'Analyse it against other positions in this portfolio',
  },
];

/**
 * Holding selector + comparison picker.
 *
 * Top row: one pill per ticker you hold — this is the "no Analysis Input step"
 * part. Below it, what that holding gets compared against, which is the single
 * value every tab keys off:
 *
 *   None        — the holding alone
 *   Competitors — its SAVED peer set. Persists per ticker, so AMD keeps its
 *                 NVDA/MU/MRVL set between sessions. Peers needn't be owned;
 *                 the point is comparing what you hold against what you don't.
 *   My Holdings — other positions here, toggled on and off. Capped, because
 *                 these endpoints are uncached and sleep-paced per ticker.
 *
 * The competitor set is never mutated by the other two modes: switching to None
 * or My Holdings hides the editor rather than clearing what you saved.
 */
export default function IntelTickerBar({
  tickers = [],
  selectedTicker,
  onSelectTicker,
  peers = [],
  onPeersChange,
  compareMode = INTEL_COMPARE_MODES.COMPETITORS,
  onCompareModeChange,
  holdingOptions = [],
  selectedHoldings = [],
  onToggleHolding,
  showCompare = true,
  maxPeers = MAX_COMPARE_PEERS,
  disabled = false,
}) {
  const [adding, setAdding] = useState(false);

  const addPeer = (option) => {
    const symbol = option?.value?.trim()?.toUpperCase();
    setAdding(false);
    if (!symbol) return;
    if (symbol === selectedTicker) return;      // comparing to itself is a no-op
    if (peers.includes(symbol)) return;
    if (peers.length >= maxPeers) return;
    onPeersChange([...peers, symbol]);
  };

  const removePeer = (symbol) => onPeersChange(peers.filter((p) => p !== symbol));

  const pillClass = (selected) =>
    `pf-seg__btn pf-seg__btn--pill whitespace-nowrap ${selected ? 'pf-seg__btn--active' : ''}`;

  if (tickers.length === 0) {
    return (
      <p className="text-sm text-muted italic">
        Add a holding to your portfolio and its companies will show up here.
      </p>
    );
  }

  // A 9th selection would be silently dropped by the slice in PortfolioIntel,
  // so stop it at the button instead of letting the click appear to do nothing.
  const atCap = selectedHoldings.length >= maxPeers;

  const renderCompetitors = () => (
    <div className="flex flex-wrap items-center gap-2 mt-1">
      {peers.map((peer) => (
        <span
          key={peer}
          className="pf-seg__btn pf-seg__btn--pill inline-flex items-center gap-1.5 cursor-default"
          style={{ padding: '5px 8px 5px 12px' }}
        >
          {peer}
          <button
            type="button"
            onClick={() => removePeer(peer)}
            disabled={disabled}
            className="text-[color:var(--pf-faint)] hover:text-red-400 transition"
            title={`Stop comparing against ${peer}`}
            style={{ lineHeight: 1, fontSize: '14px' }}
          >
            ×
          </button>
        </span>
      ))}

      {adding ? (
        <div style={{ minWidth: '220px' }}>
          {/* SearchableTicker forwards only value/onChange/placeholder
              to AsyncSelect — don't pass props it will silently drop. */}
          <SearchableTicker
            value={null}
            onChange={addPeer}
            placeholder={`Compare ${selectedTicker} to…`}
          />
        </div>
      ) : (
        peers.length < maxPeers && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            disabled={disabled}
            className="btn-ghost-primary text-xs"
          >
            + Add comparison
          </button>
        )
      )}

      {adding && (
        <button
          type="button"
          onClick={() => setAdding(false)}
          className="btn-utility text-xs"
        >
          Cancel
        </button>
      )}
    </div>
  );

  const renderHoldings = () => {
    if (holdingOptions.length === 0) {
      return (
        <p className="text-xs text-muted mt-1 italic">
          {selectedTicker} is your only holding. Add another position and you'll be able
          to compare them side by side.
        </p>
      );
    }

    return (
      <div className="pf-seg gap-2 mt-1">
        {holdingOptions.map((ticker) => {
          const included = selectedHoldings.includes(ticker);
          return (
            <button
              key={ticker}
              type="button"
              onClick={() => onToggleHolding?.(ticker)}
              disabled={disabled || (!included && atCap)}
              className={pillClass(included)}
              title={
                included
                  ? `Stop comparing against ${ticker}`
                  : atCap
                    ? `Remove one first — ${maxPeers} is the limit`
                    : `Compare against ${ticker}`
              }
            >
              {ticker}
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div className="space-y-3 mb-4">
      <div>
        <label className="filter-heading">YOUR HOLDINGS</label>
        <div className="pf-seg gap-2 mt-1">
          {tickers.map((ticker) => (
            <button
              key={ticker}
              type="button"
              onClick={() => !disabled && onSelectTicker(ticker)}
              className={pillClass(ticker === selectedTicker)}
              disabled={disabled}
            >
              {ticker}
            </button>
          ))}
        </div>
      </div>

      {selectedTicker && showCompare && (
        <div>
          <label className="filter-heading">
            COMPARE {selectedTicker} AGAINST{' '}
            <span className="text-[color:var(--pf-muted)] normal-case">
              {compareMode === INTEL_COMPARE_MODES.COMPETITORS &&
                `(saved — ${peers.length}/${maxPeers})`}
              {compareMode === INTEL_COMPARE_MODES.HOLDINGS &&
                `(${selectedHoldings.length} of ${maxPeers} max)`}
            </span>
          </label>

          <div className="pf-seg gap-2 mt-1 mb-2">
            {MODE_OPTIONS.map((option) => (
              <button
                key={option.key}
                type="button"
                onClick={() => onCompareModeChange?.(option.key)}
                disabled={disabled}
                title={option.title}
                className={`pf-seg__btn ${
                  compareMode === option.key ? 'pf-seg__btn--active' : ''
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>

          {compareMode === INTEL_COMPARE_MODES.COMPETITORS && renderCompetitors()}
          {compareMode === INTEL_COMPARE_MODES.HOLDINGS && renderHoldings()}

          {compareMode === INTEL_COMPARE_MODES.COMPETITORS &&
            peers.length === 0 &&
            !adding && (
              <p className="text-xs text-muted mt-1 italic">
                No comparisons saved. Add the competitors you usually check {selectedTicker}{' '}
                against — they'll be here next time.
              </p>
            )}
        </div>
      )}
    </div>
  );
}
